import type { RefObject } from 'react'

import type { LaneActions } from '@/modules/projects/components/Workbench/AccountLaneHeader'
import type { StatusFor } from '@/modules/projects/components/Workbench/coverage'
import { LoadingLanes } from '@/modules/projects/components/Workbench/LoadingLanes'
import type {
  MapLane,
  SplitMap,
} from '@/modules/projects/components/Workbench/map'
import {
  type MapSection,
  mapSections,
} from '@/modules/projects/components/Workbench/mapBlocks'
import type {
  Highlight,
  MapActions,
  MapFilters,
} from '@/modules/projects/components/Workbench/mapView'
import type { DocumentPage } from '@/modules/projects/components/Workbench/types'
import { MonthNames } from './MonthNames'
import { SectionView } from './SectionView'
import { Unplaced } from './Unplaced'
import { useMapFollows } from './useMapFollows'

interface AccountMapProps {
  map: SplitMap
  pagesOf: (pages: number[]) => DocumentPage[]
  choosing: boolean
  openStatementId: string | null
  // Only a statement the user opened. On arrival none is, so the map starts at the top.
  followStatementId: string | null
  filters: MapFilters
  statusFor: StatusFor
  scrollRef: RefObject<HTMLDivElement | null>
  loading: boolean
  highlight: Highlight
  actions: MapActions
  laneActions: LaneActions
}

const keyOf = (lane: MapLane) => lane.accountId ?? 'none'

const sectionKey = (section: MapSection) =>
  [...section.lanes, ...section.members].map(keyOf).join('+')

export const AccountMap = ({
  map,
  pagesOf,
  choosing,
  openStatementId,
  followStatementId,
  filters,
  statusFor,
  scrollRef,
  loading,
  highlight,
  actions,
  laneActions,
}: AccountMapProps) => {
  useMapFollows(map.lanes, followStatementId, filters, scrollRef)

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      {map.unplaced.length > 0 ? (
        <Unplaced
          views={map.unplaced}
          pagesOf={pagesOf}
          choosing={choosing}
          openStatementId={openStatementId}
          actions={actions}
        />
      ) : null}

      <div
        ref={scrollRef}
        data-map-pane
        className="flex min-h-0 flex-1 flex-col overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-300"
      >
        <MonthNames />

        {loading ? <LoadingLanes /> : null}

        {loading
          ? null
          : mapSections(map.lanes).map((section) => (
              <SectionView
                key={sectionKey(section)}
                section={section}
                choosing={choosing}
                openStatementId={openStatementId}
                highlight={highlight}
                statusFor={statusFor}
                actions={actions}
                laneActions={laneActions}
              />
            ))}
      </div>
    </div>
  )
}
